import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { supportApi, formatApiError } from '../lib/api';
import { useNotifications } from '../context/NotificationContext';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, HelpCircle, CheckCircle, User, Shield, Mail } from 'lucide-react';

const statusColors = {
  open: 'bg-yellow-100 text-yellow-700',
  replied: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700'
};

const statusLabels = {
  open: 'Waiting for reply',
  replied: 'Replied',
  closed: 'Closed'
};

export default function SupportChatPage() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { refreshSupportCount } = useNotifications();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await supportApi.getMessages(ticketId);
      setMessages(prev => {
        if (data.length !== prev.length) {
          return data;
        }
        return prev;
      });
    } catch {
      // silently fail on poll
    }
  }, [ticketId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ticketRes, messagesRes] = await Promise.all([
          supportApi.getTicket(ticketId),
          supportApi.getMessages(ticketId)
        ]);
        setTicket(ticketRes.data);
        setMessages(messagesRes.data);
        setIsAdmin(ticketRes.data.is_admin);
      } catch (err) {
        toast.error(formatApiError(err));
        navigate('/support');
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    // Poll for new messages every 4 seconds
    pollRef.current = setInterval(fetchMessages, 4000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [ticketId, navigate, fetchMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending || ticket?.status === 'closed') return;

    setSending(true);
    try {
      const { data } = await supportApi.sendMessage(ticketId, newMessage.trim());
      setMessages(prev => [...prev, data]);
      setNewMessage('');
      setTicket(prev => ({
        ...prev,
        status: isAdmin ? 'replied' : 'open'
      }));
      // Refresh admin support count after replying
      if (isAdmin) refreshSupportCount();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const handleCloseTicket = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) return;
    
    try {
      await supportApi.closeTicket(ticketId);
      setTicket(prev => ({ ...prev, status: 'closed' }));
      toast.success('Ticket closed');
      if (isAdmin) refreshSupportCount();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-CA', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="support-chat-loading">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="support-chat-page">
      {/* Header */}
      <div className="bg-background border-b border-border sticky top-16 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/support')}
                className="p-2 -ml-2 hover:bg-muted rounded-lg transition-colors"
                data-testid="back-to-support"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <HelpCircle className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {ticket?.subject}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={statusColors[ticket?.status] || 'bg-gray-100'}>
                      {statusLabels[ticket?.status] || ticket?.status}
                    </Badge>
                    {isAdmin && ticket?.user_name && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {ticket.user_name}
                      </span>
                    )}
                    {isAdmin && ticket?.user_email && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {ticket.user_email}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {isAdmin && ticket?.status !== 'closed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleCloseTicket}
                className="gap-2"
                data-testid="close-ticket-btn"
              >
                <CheckCircle className="h-4 w-4" />
                Close Ticket
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-6">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
              <p className="text-muted-foreground">No messages yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => {
                const isSupport = message.sender_type === 'admin';
                return (
                  <div
                    key={message.id}
                    className={`flex ${message.is_mine ? 'justify-end' : 'justify-start'}`}
                    data-testid={`support-message-${message.id}`}
                  >
                    <div className={`flex items-start gap-3 max-w-[80%] sm:max-w-[70%] ${message.is_mine ? 'flex-row-reverse' : ''}`}>
                      {/* Avatar */}
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isSupport ? 'bg-primary text-primary-foreground' : 'bg-muted'
                      }`}>
                        {isSupport ? (
                          <Shield className="h-4 w-4" />
                        ) : (
                          <User className="h-4 w-4" />
                        )}
                      </div>
                      
                      {/* Message */}
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-medium ${isSupport ? 'text-primary' : 'text-muted-foreground'}`}>
                            {isSupport ? 'Support Team' : message.sender_name}
                          </span>
                        </div>
                        <div
                          className={`rounded-2xl px-4 py-3 ${
                            message.is_mine
                              ? 'bg-primary text-primary-foreground rounded-br-md'
                              : isSupport
                              ? 'bg-primary/10 rounded-bl-md'
                              : 'bg-muted rounded-bl-md'
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words">{message.content}</p>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 px-1">
                          {formatTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Message Input */}
      {ticket?.status !== 'closed' ? (
        <div className="bg-background border-t border-border sticky bottom-0">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <form onSubmit={handleSend} className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isAdmin ? "Type your reply..." : "Type a message..."}
                className="flex-1"
                disabled={sending}
                data-testid="support-message-input"
              />
              <Button 
                type="submit" 
                disabled={!newMessage.trim() || sending}
                data-testid="send-support-message-btn"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </form>
          </div>
        </div>
      ) : (
        <div className="bg-muted/50 border-t border-border">
          <div className="max-w-4xl mx-auto px-4 py-4 text-center">
            <p className="text-sm text-muted-foreground">
              This ticket has been closed. Need more help? 
              <button 
                onClick={() => navigate('/support')}
                className="text-primary hover:underline ml-1"
              >
                Create a new request
              </button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
